import type { OptionValue } from '@/components/organisms/Select/types';
import type { Filters } from '@/context/useFilters/types';

import { getCustomFilterEntries } from './parsers';

import type { CustomFilterApiKeySet, HazardousFilter, NewFilters } from './types';

export type FiltersPayload = Partial<{
  countries: OptionValue[];
  disposers: string[];
  eurals: string[];
  hazardous: boolean;
  locations: string[];
  materials: string[];
  period: NewFilters['period'];
  processes: number[];
  processors: string[];
  projects: OptionValue[];
  radius: OptionValue[];
}> &
  Record<string, OptionValue[] | boolean | NewFilters['period'] | undefined>;

function isProcessLeafValue(value: OptionValue): value is number {
  return typeof value === 'number';
}

export function toFiltersPayload(
  filters: Partial<NewFilters>,
  allowedCustomFilterKeys?: CustomFilterApiKeySet
): FiltersPayload {
  const payload: FiltersPayload = {};
  const {
    countries = [],
    disposers = [],
    eurals = [],
    hazardous,
    locations = [],
    materials = [],
    period = [],
    processes = [],
    processors = [],
    projects = [],
    radius = [],
  } = filters;
  const filteredDisposers = disposers.filter((value) => String(value).includes('-')).map(String);
  const filteredProcesses = processes.filter(isProcessLeafValue);

  if (countries.length > 0) payload.countries = countries;
  if (filteredDisposers.length > 0) payload.disposers = filteredDisposers;
  if (eurals.length > 0) payload.eurals = eurals.map(String);
  if (hazardous !== null && hazardous !== undefined) payload.hazardous = hazardous === '1';
  if (locations.length > 0) payload.locations = locations.map(String);
  if (materials.length > 0) payload.materials = materials.map(String);
  if (period.length > 0) payload.period = period;
  if (filteredProcesses.length > 0) payload.processes = filteredProcesses;
  if (processors.length > 0) payload.processors = processors.map(String);
  if (projects.length > 0) payload.projects = projects;
  if (radius.length > 0) payload.radius = radius;

  for (const [key, value] of getCustomFilterEntries(filters, allowedCustomFilterKeys)) {
    if (value.length > 0) payload[key] = value;
  }

  return payload;
}

export function stringifyFiltersPayload(
  filters: Partial<NewFilters>,
  allowedCustomFilterKeys?: CustomFilterApiKeySet
): string {
  return JSON.stringify(toFiltersPayload(filters, allowedCustomFilterKeys));
}

function toOptionValueArray(value: unknown): OptionValue[] | undefined {
  if (Array.isArray(value)) {
    return value.filter((item): item is OptionValue => typeof item === 'string' || typeof item === 'number');
  }

  if (typeof value === 'string' || typeof value === 'number') return [value];

  return undefined;
}

function toStringValueArray(value: unknown): string[] | undefined {
  return toOptionValueArray(value)?.map(String);
}

function toProcessValueArray(value: unknown): OptionValue[] | undefined {
  return toOptionValueArray(value)?.map((item) => {
    if (typeof item === 'number') return item;

    const numericValue = Number(item);

    return Number.isInteger(numericValue) ? numericValue : item;
  });
}

function toHazardousFilter(value: unknown): HazardousFilter | null | undefined {
  if (value === true) return '1';
  if (value === false) return '0';
  if (value === '1' || value === '0' || value === null) return value;
  return undefined;
}

export function toFiltersPatch(filters: Partial<Filters>): Partial<NewFilters> {
  const patch: Partial<NewFilters> = {};
  const countries = toStringValueArray(filters?.countries);
  const disposers = toStringValueArray(filters?.disposers);
  const eurals = toStringValueArray(filters?.eurals);
  const hazardous = toHazardousFilter(filters?.hazardous);
  const locations = toStringValueArray(filters?.locations);
  const materials = toStringValueArray(filters?.materials);
  const processes = toProcessValueArray(filters?.processes);
  const processors = toStringValueArray(filters?.processors);
  const projects = toStringValueArray(filters?.projects);

  if (countries) patch.countries = countries;
  if (disposers) patch.disposers = disposers;
  if (eurals) patch.eurals = eurals;
  if (hazardous !== undefined) patch.hazardous = hazardous;
  if (locations) patch.locations = locations;
  if (materials) patch.materials = materials;
  if (filters?.period) patch.period = filters?.period;
  if (processes) patch.processes = processes;
  if (processors) patch.processors = processors;
  if (projects) patch.projects = projects;

  return patch;
}
